import React, { useEffect, useState } from 'react';
import axios from '../api/axios';
import styles from './MyFeedback.module.css';
import { MessageSquare, AlertCircle, CheckCircle, Clock, ChevronDown, ChevronUp } from 'lucide-react';

interface Feedback {
  id: number;
  userId: number;
  userName: string;
  userEmail: string;
  pageScope: string;
  category: string;
  message: string;
  priority: string | null;
  status: string;
  createdAt: string;
  updatedAt: string;
  responses: FeedbackResponse[];
}

interface FeedbackResponse {
  id: number;
  adminUserId: number;
  adminName: string;
  responseText: string;
  respondedAt: string;
}

const MyFeedback: React.FC = () => {
  const [feedbacks, setFeedbacks] = useState<Feedback[]>([]);
  const [loading, setLoading] = useState(true);
  const [expandedId, setExpandedId] = useState<number | null>(null);

  useEffect(() => {
    fetchMyFeedback();
  }, []);

  useEffect(() => {
    // Highlight the feedback button if user has no feedback
    if (!loading && feedbacks.length === 0) {
      const timer = setTimeout(() => {
        const button = document.querySelector('[data-feedback-button]') as HTMLElement;
        if (button) {
          button.style.animation = 'pulse 2s ease-in-out 3';
          setTimeout(() => {
            if (button) button.style.animation = '';
          }, 6000); // Remove after 3 pulses (2s × 3 = 6s)
        }
      }, 500);
      return () => clearTimeout(timer);
    }
  }, [loading, feedbacks.length]);

  const fetchMyFeedback = async () => {
    try {
      const response = await axios.get('/feedback/my');
      setFeedbacks(response.data);
    } catch (error) {
      console.error('Failed to fetch my feedback:', error);
    } finally {
      setLoading(false);
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'Open':
        return <AlertCircle size={18} className={styles.statusIconOpen} />;
      case 'In Review':
        return <Clock size={18} className={styles.statusIconReview} />;
      case 'Completed':
        return <CheckCircle size={18} className={styles.statusIconCompleted} />;
      default:
        return null;
    }
  };

  const getStatusClass = (status: string) => {
    switch (status) {
      case 'Open':
        return styles.statusOpen;
      case 'In Review':
        return styles.statusReview;
      case 'Completed':
        return styles.statusCompleted;
      default:
        return '';
    }
  };

  const getPriorityClass = (priority: string | null) => {
    switch (priority) {
      case 'High':
        return styles.priorityHigh;
      case 'Medium':
        return styles.priorityMedium;
      case 'Low':
        return styles.priorityLow;
      default:
        return '';
    }
  };

  const toggleExpand = (id: number) => {
    setExpandedId(expandedId === id ? null : id);
  };

  if (loading) {
    return <div className={styles.loading}>Loading your feedback...</div>;
  }

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <div className={styles.headerTitle}>
          <MessageSquare size={28} />
          <h1>My Feedback</h1>
        </div>
        <p className={styles.subtitle}>
          {feedbacks.length} {feedbacks.length === 1 ? 'submission' : 'submissions'}
        </p>
      </div>

      {feedbacks.length === 0 ? (
        <div className={styles.emptyState}>
          <MessageSquare size={56} />
          <h2>No feedback yet</h2>
          <p>You haven't submitted any feedback. Click the feedback button in the bottom right corner to share your thoughts!</p>
        </div>
      ) : (
        <div className={styles.feedbackList}>
          {feedbacks.map(feedback => (
            <div key={feedback.id} className={styles.feedbackCard}>
              <div className={styles.cardHeader} onClick={() => toggleExpand(feedback.id)}>
                <div className={styles.cardMeta}>
                  <span className={`${styles.status} ${getStatusClass(feedback.status)}`}>
                    {getStatusIcon(feedback.status)}
                    {feedback.status}
                  </span>
                  <span className={styles.category}>{feedback.category}</span>
                  {feedback.priority && (
                    <span className={`${styles.priority} ${getPriorityClass(feedback.priority)}`}>
                      {feedback.priority} Priority
                    </span>
                  )}
                </div>
                <button className={styles.expandBtn} aria-label="Expand">
                  {expandedId === feedback.id ? (
                    <ChevronUp size={20} />
                  ) : (
                    <ChevronDown size={20} />
                  )}
                </button>
              </div>

              <div className={styles.cardBody}>
                <div className={styles.cardInfo}>
                  <span className={styles.infoLabel}>Page:</span>
                  <span>{feedback.pageScope}</span>
                </div>
                <div className={styles.cardInfo}>
                  <span className={styles.infoLabel}>Submitted:</span>
                  <span>{new Date(feedback.createdAt).toLocaleString()}</span>
                </div>
                
                <div className={styles.messageSection}>
                  <h3>Your Message</h3>
                  <div className={styles.messageBox}>{feedback.message}</div>
                </div>

                {expandedId === feedback.id && feedback.responses.length > 0 && (
                  <div className={styles.responsesSection}>
                    <h3>Admin Responses ({feedback.responses.length})</h3>
                    {feedback.responses.map(response => (
                      <div key={response.id} className={styles.responseCard}>
                        <div className={styles.responseHeader}>
                          <strong>{response.adminName}</strong>
                          <span className={styles.responseDate}>
                            {new Date(response.respondedAt).toLocaleString()}
                          </span>
                        </div>
                        <div className={styles.responseBody}>
                          {response.responseText}
                        </div>
                      </div>
                    ))}
                  </div>
                )}

                {expandedId === feedback.id && feedback.responses.length === 0 && (
                  <div className={styles.noResponses}>
                    <Clock size={20} />
                    <span>Awaiting admin response</span>
                  </div>
                )}
              </div>

              {feedback.responses.length > 0 && expandedId !== feedback.id && (
                <div className={styles.responseIndicator}>
                  <CheckCircle size={16} />
                  {feedback.responses.length} {feedback.responses.length === 1 ? 'response' : 'responses'} available
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default MyFeedback;
